/**
 * IntakeWizardDialog Component
 * 
 * Extracted from App.js - This is the Add/Edit Client dialog
 * Pure extraction with no functional or UI changes.
 * 
 * This component renders the exact same dialog as previously existed in App.js.
 * All state and handlers remain in App.js and are passed as props.
 * 
 * Enhanced to support inline insurance management during new client creation.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { AllergiesSection } from './AllergiesSection';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
// Plan A perf: replace per-keystroke parent-state updates with a 300ms
// local debounce so typing doesn't reconcile the entire 2,766-line
// IntakeWizardDialog component on every character. Flush-on-unmount is
// already in place inside Debounced* — zero data-loss risk.
import { DebouncedTextarea, DebouncedInput } from '../ui/debounced-textarea';
import { InsuranceCompanyOptions } from './InsuranceCompanyOptions';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
  Users, RefreshCw, Search, X, Eye, Edit, 
  CreditCard, Plus, Archive, Trash2, AlertTriangle, History
} from 'lucide-react';
import { SYMPTOM_OPTIONS, URGENT_SYMPTOMS } from '../../constants';
import {
  fmtLocalDate,
  isInsuranceExpired,
  isInsuranceEndingSoon,
} from '../../utils/insuranceDates';
import { useZipAutofill } from '../../hooks/useZipAutofill';
import { getAgeClassification } from '../../utils/clientHelpers';
import { formatClientStatus } from '../../utils/displayFormat';
import {
  validateEmail, validatePhone, validateZipCode, validateSSN, validateDateOfBirth,
} from '../../utils/intakeValidators';
import { DateField } from './DateTimeField';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IntakeWizardDialog = ({
  // Dialog state
  showIntakeWizard,
  setShowIntakeWizard,
  
  // Client data
  editingClient,
  intakeData,
  intakeClientId,
  intakeLastSaved,
  intakeAutoSaving,
  intakeValidationErrors,
  
  // Duplicate checking
  checkingRealtimeDuplicates,
  setCheckingRealtimeDuplicates,
  realtimeDuplicateWarnings,
  setRealtimeDuplicateWarnings,
  
  // Change request state
  setShowChangeRequestDialog,
  setChangeRequestProposedChanges,
  setChangeRequestSection,
  setChangeRequestFields,
  
  // Provider assignment state
  providers,
  setProviders,
  setSelectedProvidersForAssignment,
  setShowAssignProviders,
  setAvailableServices,
  setExistingEnrollments,
  setProviderServiceAssignments,
  
  // Client selection
  setSelectedClient,
  setClientDetailTab,
  setShowClientDetail,
  
  // Reference data
  locations,
  users,
  serviceTypes = [],
  onViewChangeHistory,
  
  // Handlers
  handleIntakeFieldChange,
  handleIntakeFieldValidation,
  handleCloseIntakeWizard,
  handleSaveIntakeDraft,
  handleGenerateIntakePDF,
  handleCompleteIntake,
  handleOpenIntakeWizard,
  handleOpenInsuranceManagement,
  
  // Permission/config helpers
  canEditFieldCategory,
  isFieldVisible,
  isFieldRequired,
  hasPermission,
  
  // Pending insurances for new client creation
  pendingInsurances = [],
  setPendingInsurances,
  
  // Medication dialog props for editing existing client
  setShowMedicationForm,
  setEditingMedication,
  clientMedications
}) => {
  // Local state for insurance form dialog
  const [showInsuranceForm, setShowInsuranceForm] = useState(false);
  const [editingInsuranceIndex, setEditingInsuranceIndex] = useState(null);
  const [insuranceCompanies, setInsuranceCompanies] = useState([]);
  const [insuranceSearchTerm, setInsuranceSearchTerm] = useState('');
  const [selectedInsuranceCompany, setSelectedInsuranceCompany] = useState(null);
  const [insuranceFormState, setInsuranceFormState] = useState({
    company: '',
    plan: '',
    member_id: '',
    group_number: '',
    phone: '',
    relationship_to_insured: 'self',
    insured_name: '',
    insured_ssn: '',
    insured_dob: '',
    copay_amount: '',
    deductible_amount: '',
    priority: 1,
    start_date: '',
    end_date: '',
    eligibility_status: 'pending',
    msp_type_code: '12'
  });

  // ============================================================
  // LOCAL DRAFT STATE (Perf fix — prevents App.js re-renders on every keystroke)
  // ------------------------------------------------------------
  // The parent (App.js) used to own intakeData state directly. Every keystroke
  // triggered a setState in the 82k-line App.js component, causing full-tree
  // re-renders and visible typing lag — especially with 1500+ clients in state.
  //
  // Now: this dialog owns its own draft state (`formData`). The parent seeds
  // it via the `intakeData` prop when the dialog opens, and receives the final
  // draft only on Save Draft / Complete / Generate PDF button clicks.
  // ============================================================
  const [formData, setFormData] = useState(intakeData || {});

  // Seed local draft from parent ONLY when the dialog opens (or editingClient
  // changes — e.g., navigating between edit targets without closing). We do
  // NOT sync on every parent intakeData change (that would defeat the fix).
  useEffect(() => {
    if (showIntakeWizard) {
      const seeded = { ...(intakeData || {}) };
      // Default state to MA for new clients (practice is MA-restricted).
      // Only apply when the field is empty so we don't clobber an imported value.
      if (!editingClient) {
        if (!seeded.state) seeded.state = 'MA';
        if (!seeded.insurance_state) seeded.insurance_state = 'MA';
        if (!seeded.guardian_state) seeded.guardian_state = 'MA';
      }
      setFormData(seeded);
    }
    // Intentionally omitting `intakeData` from deps — we only want to re-seed
    // on dialog open or when switching edit targets, not on every parent change.
  }, [showIntakeWizard, editingClient?.id]); // eslint-disable-line

  // Sync specific "collection" fields from parent `intakeData` into local `formData`
  // whenever the parent writes them via sub-dialogs (Assign Services, Medications,
  // etc., which live in App.js and call `handleIntakeFieldChange`). Typed text
  // fields are NOT synced here — they continue to live in local formData only,
  // preserving the typing-lag fix. We only sync fields that are written by
  // external (non-keystroke) flows.
  useEffect(() => {
    if (!showIntakeWizard) return;
    const syncedFields = [
      'assigned_providers',
      'pending_service_enrollments',
      'medications',
      'symptoms_checklist',
    ];
    setFormData(prev => {
      let changed = false;
      const next = { ...prev };
      for (const k of syncedFields) {
        const parentVal = intakeData ? intakeData[k] : undefined;
        if (parentVal !== undefined && parentVal !== prev[k]) {
          next[k] = parentVal;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [
    showIntakeWizard,
    intakeData?.assigned_providers,
    intakeData?.pending_service_enrollments,
    intakeData?.medications,
    intakeData?.symptoms_checklist,
  ]);

  // Local field-change handler — updates ONLY dialog state. Fast, no App.js re-render.
  const handleFieldChange = (field, value) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      // Conditional logic the old handler had: clear guardian fields when has_guardian goes false
      if (field === 'has_guardian' && value === false) {
        next.guardian_name = '';
        next.guardian_phone = '';
        next.guardian_email = '';
        next.guardian_same_household = false;
        next.guardian_is_emergency_contact = false;
      }
      // This mirrors the parent handleIntakeFieldChange logic that existed before the refactor.
      if (field === 'first_name' || field === 'middle_name' || field === 'last_name') {
        const firstName = field === 'first_name' ? value : (prev.first_name || '');
        const middleName = field === 'middle_name' ? value : (prev.middle_name || '');
        const lastName = field === 'last_name' ? value : (prev.last_name || '');
        next.full_name = `${firstName} ${middleName} ${lastName}`.trim().replace(/\s+/g, ' ');
      }
      return next;
    });
  };
  // eslint-disable-next-line no-unused-vars
  const _unused_prop_handleIntakeFieldChange = handleIntakeFieldChange; // prevent eslint unused-prop warning

  // ── ZIP → City/State autofill (June 2026) ───────────────────────
  // Two instances: one for client address, one for the insurance
  // address block.  Each hook only fills city/state if empty.
  const setField = (name, val) => handleFieldChange(name, val);
  const clientZip = useZipAutofill({
    formData, setField,
    zipField: 'zipcode', cityField: 'city', stateField: 'state',
  });
  const insuranceZip = useZipAutofill({
    formData, setField,
    zipField: 'insurance_zipcode', cityField: 'insurance_city', stateField: 'insurance_state',
  });

  // Ref to always expose the latest formData to long-lived closures (autosave).
  const formDataRef = React.useRef(formData);
  React.useEffect(() => { formDataRef.current = formData; }, [formData]);

  // Autosave — runs every 60s while dialog is open with a client ID and not
  // in edit mode. Reads the latest formData via ref so the interval dep array
  // doesn't reset on every keystroke (which was the pre-fix behavior).
  useEffect(() => {
    if (!showIntakeWizard || !intakeClientId || editingClient) return;
    const id = setInterval(async () => {
      try {
        await axios.post(`${API}/clients/${intakeClientId}/intake-draft`, {
          current_step: 1,
          data: formDataRef.current
        });
      } catch (e) {
        console.error('Autosave failed:', e);
      }
    }, 60000);
    return () => clearInterval(id);
  }, [showIntakeWizard, intakeClientId, editingClient]);

  // Fetch insurance companies when dialog opens (for new client insurance management)
  useEffect(() => {
    if (showIntakeWizard && !editingClient) {
      fetchInsuranceCompanies();
    }
  }, [showIntakeWizard, editingClient]);
  
  // Fetch existing medications when editing a client
  useEffect(() => {
    const fetchClientMedications = async () => {
      if (showIntakeWizard && editingClient && editingClient.id) {
        try {
          const response = await axios.get(`${API}/clients/${editingClient.id}/medications`);
          const activeMeds = (response.data || []).filter(m => m.is_active !== false);
          if (activeMeds.length > 0) {
            handleFieldChange('medications', activeMeds.map(m => ({
              id: m.id,
              name: m.name || m.medication_name || '',
              dosage: m.dosage || '',
              frequency: m.frequency || '',
              prescriber: m.prescriber || '',
              is_active: true
            })));
          }
        } catch (error) {
          console.error('Error fetching client medications:', error);
        }
      }
    };
    fetchClientMedications();
  }, [showIntakeWizard, editingClient]);

  const fetchInsuranceCompanies = async () => {
    try {
      const response = await axios.get(`${API}/insurances`);
      setInsuranceCompanies(response.data);
    } catch (error) {
      console.error('Error fetching insurance companies:', error);
    }
  };

  // Handler: Open add insurance form for new client
  const handleAddPendingInsurance = () => {
    setEditingInsuranceIndex(null);
    setSelectedInsuranceCompany(null);
    
    // Auto-calculate next priority
    const nextPriority = pendingInsurances.length > 0 
      ? Math.max(...pendingInsurances.map(ins => ins.priority || 1)) + 1 
      : 1;
    
    setInsuranceFormState({
      company: '',
      plan: '',
      member_id: '',
      group_number: '',
      phone: '',
      relationship_to_insured: 'self',
      insured_name: '',
      insured_ssn: '',
      insured_dob: '',
      copay_amount: '',
      deductible_amount: '',
      priority: nextPriority,
      start_date: '',
      end_date: '',
      eligibility_status: 'pending',
      msp_type_code: '12'
    });
    setShowInsuranceForm(true);
  };

  // Handler: Edit pending insurance
  const handleEditPendingInsurance = (index) => {
    const insurance = pendingInsurances[index];
    setEditingInsuranceIndex(index);
    const insuranceCompany = insuranceCompanies.find(ins => ins.name === insurance.company);
    setSelectedInsuranceCompany(insuranceCompany);
    setInsuranceFormState({
      company: insurance.company || '',
      plan: insurance.plan || '',
      member_id: insurance.member_id || '',
      group_number: insurance.group_number || '',
      phone: insurance.phone || '',
      relationship_to_insured: insurance.relationship_to_insured || 'self',
      insured_name: insurance.insured_name || '',
      insured_ssn: insurance.insured_ssn || '',
      insured_dob: insurance.insured_dob || '',
      copay_amount: insurance.copay_amount || '',
      deductible_amount: insurance.deductible_amount || '',
      priority: insurance.priority || 1,
      start_date: insurance.start_date || '',
      end_date: insurance.end_date || '',
      eligibility_status: insurance.eligibility_status || 'pending',
      msp_type_code: insurance.msp_type_code || '12'
    });
    setShowInsuranceForm(true);
  };

  // Handler: Save pending insurance (add or update)
  const handleSavePendingInsurance = () => {
    if (!insuranceFormState.company) {
      toast.error('Insurance Company is required');
      return;
    }
    if (!insuranceFormState.member_id) {
      toast.error('Member ID is required');
      return;
    }

    const insuranceData = { ...insuranceFormState, status: 'active' };

    if (editingInsuranceIndex !== null) {
      // Update existing
      const updated = [...pendingInsurances];
      updated[editingInsuranceIndex] = insuranceData;
      setPendingInsurances(updated);
      toast.success('Insurance updated');
    } else {
      // Add new
      setPendingInsurances([...pendingInsurances, insuranceData]);
      toast.success('Insurance added');
    }
    setShowInsuranceForm(false);
  };

  // Handler: Remove pending insurance
  const handleRemovePendingInsurance = (index) => {
    const updated = pendingInsurances.filter((_, i) => i !== index);
    // Recalculate priorities
    const reordered = updated.map((ins, i) => ({ ...ins, priority: i + 1 }));
    setPendingInsurances(reordered);
    toast.success('Insurance removed');
  };

  // Priority labels and colors for display
  const priorityLabels = {1: 'Primary', 2: 'Secondary', 3: 'Tertiary', 4: 'Quaternary'};
  const priorityColors = {
    1: 'bg-blue-50 border-blue-200',
    2: 'bg-green-50 border-green-200',
    3: 'bg-purple-50 border-purple-200',
    4: 'bg-orange-50 border-orange-200'
  };

  return (
    <>
    <Dialog open={showIntakeWizard} onOpenChange={setShowIntakeWizard}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            {editingClient ? `Edit Client - ${editingClient.full_name}` : `Client Intake Form`}
            {(() => {
              const ac = getAgeClassification(formData.date_of_birth || editingClient?.date_of_birth);
              return ac ? (
                <span
                  className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${ac.cls}`}
                  title={`Age ${ac.age} — computed from date of birth`}
                  data-testid="edit-client-age-classification"
                >
                  {ac.label}
                </span>
              ) : null;
            })()}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Edit client demographics, insurance, and intake data.
          </DialogDescription>
          <div className="flex items-center justify-between text-sm text-gray-500 mt-2">
            <div>
              {!editingClient && intakeLastSaved && (
                <span>Last saved: {intakeLastSaved.toLocaleTimeString()}</span>
              )}
              {!editingClient && intakeAutoSaving && <span className="ml-2 text-blue-600">Saving...</span>}
            </div>
          </div>
        </DialogHeader>

        {/* Manual Duplicate Check Section */}
        {!editingClient && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <span className="text-sm text-blue-900">
                  Check if this client already exists in the system
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                disabled={checkingRealtimeDuplicates || !formData.first_name?.trim() || !formData.last_name?.trim()}
                onClick={async () => {
                  if (!formData.first_name?.trim() || !formData.last_name?.trim()) {
                    toast.error("Please enter first and last name first");
                    return;
                  }
                  
                  setCheckingRealtimeDuplicates(true);
                  try {
                    const fullName = `${formData.first_name.trim()} ${formData.last_name.trim()}`;
                    const params = new URLSearchParams();
                    params.append('full_name', fullName);
                    if (formData.date_of_birth) params.append('date_of_birth', formData.date_of_birth);
                    if (formData.ssn) params.append('ssn', formData.ssn);
                    if (formData.email) params.append('email', formData.email);
                    if (formData.phone) params.append('phone', formData.phone);
                    
                    const response = await axios.post(`${API}/clients/check-duplicates?${params.toString()}`, {}, {
                      timeout: 10000
                    });
                    
                    if (response.data?.has_duplicates) {
                      setRealtimeDuplicateWarnings(response.data.duplicates.slice(0, 5));
                      toast.warning(`Found ${response.data.duplicates.length} potential match(es)`);
                    } else {
                      setRealtimeDuplicateWarnings([]);
                      toast.success("No matching clients found");
                    }
                  } catch (error) {
                    console.error('Duplicate check failed:', error);
                    toast.error("Failed to check for duplicates");
                  } finally {
                    setCheckingRealtimeDuplicates(false);
                  }
                }}
              >
                {checkingRealtimeDuplicates ? (
                  <>
                    <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                    Checking...
                  </>
                ) : (
                  <>
                    <Search className="h-3 w-3 mr-1" />
                    Check for Duplicates
                  </>
                )}
              </Button>
            </div>
            
            {/* Show results if any */}
            {realtimeDuplicateWarnings.length > 0 && (
              <div className="mt-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-yellow-800">⚠️ Potential matches found:</p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => setRealtimeDuplicateWarnings([])}
                  >
                    <X className="h-3 w-3 mr-1" />
                    Dismiss
                  </Button>
                </div>
                {realtimeDuplicateWarnings.map((dup) => (
                  <div key={dup.id} className="flex items-center justify-between bg-white rounded p-2 border border-yellow-200">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={
                        dup.confidence === 'exact' ? 'bg-red-600' :
                        dup.confidence === 'strong' ? 'bg-orange-600' :
                        dup.confidence === 'likely' ? 'bg-yellow-600' : 'bg-blue-600'
                      }>
                        {dup.confidence_label}
                      </Badge>
                      <span className="font-medium">{dup.full_name}</span>
                      <span className="text-xs text-gray-500">
                        DOB: {dup.date_of_birth || 'N/A'} | Status: {formatClientStatus(dup.status)}
                        {dup.ssn_masked && ` | SSN: ${dup.ssn_masked}`}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 px-2"
                        onClick={async () => {
                          try {
                            const response = await axios.get(`${API}/clients/${dup.id}`);
                            setSelectedClient(response.data);
                            setClientDetailTab("overview");
                            setShowClientDetail(true);
                          } catch (error) {
                            toast.error("Failed to open client");
                          }
                        }}
                      >
                        <Eye className="h-3 w-3 mr-1" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-green-700 border-green-300"
                        onClick={() => {
                          setShowIntakeWizard(false);
                          setRealtimeDuplicateWarnings([]);
                          handleOpenIntakeWizard(dup.id);
                          toast.info("Opened existing client for editing");
                        }}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Use
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="mt-6 space-y-8">
          {/* Section 1: Basic Information */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold">Basic Information</h3>
              {!canEditFieldCategory('demographics') && editingClient && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={() => {
                    setShowChangeRequestDialog(true); setChangeRequestProposedChanges({});
                    setChangeRequestSection('demographics');
                    setChangeRequestFields(['first_name', 'middle_name', 'last_name', 'preferred_name', 'date_of_birth', 'gender', 'email', 'phone', 'marital_status', 'address_line1', 'address_line2', 'city', 'state', 'zipcode', 'race', 'ethnicity', 'occupation', 'language']);
                  }}
                >
                  Request Change
                </Button>
              )}
            </div>
              <div className="grid grid-cols-3 gap-4">
                {isFieldVisible('first_name') && (
                  <div>
                    <Label>First Name {isFieldRequired('first_name') && '*'}</Label>
                    <DebouncedInput
                      value={formData.first_name || ''}
                      onChange={(e) => handleFieldChange('first_name', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      required={isFieldRequired('first_name')}
                    />
                  </div>
                )}
                {isFieldVisible('middle_name') && (
                  <div>
                    <Label>Middle Name {isFieldRequired('middle_name') && '*'}</Label>
                    <DebouncedInput
                      value={formData.middle_name || ''}
                      onChange={(e) => handleFieldChange('middle_name', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      required={isFieldRequired('middle_name')}
                    />
                  </div>
                )}
                {isFieldVisible('last_name') && (
                  <div>
                    <Label>Last Name {isFieldRequired('last_name') && '*'}</Label>
                    <DebouncedInput
                      value={formData.last_name || ''}
                      onChange={(e) => handleFieldChange('last_name', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      required={isFieldRequired('last_name')}
                    />
                  </div>
                )}
              </div>
              
              {isFieldVisible('preferred_name') && (
                <div>
                  <Label>Preferred Name {isFieldRequired('preferred_name') && '*'}</Label>
                  <DebouncedInput
                    value={formData.preferred_name || ''}
                    onChange={(e) => handleFieldChange('preferred_name', e.target.value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    placeholder="If different from first name"
                    required={isFieldRequired('preferred_name')}
                  />
                </div>
              )}
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Date of Birth *</Label>
                  <DateField
                    value={formData.date_of_birth || ''}
                    onChange={(v) => {
                      handleFieldChange('date_of_birth', v);
                      handleIntakeFieldValidation('date_of_birth', validateDateOfBirth(v));
                    }}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    invalid={!!intakeValidationErrors.date_of_birth}
                    testId="intake-dob-input"
                  />
                  {intakeValidationErrors.date_of_birth && (
                    <p className="text-sm text-red-600 mt-1">{intakeValidationErrors.date_of_birth}</p>
                  )}
                </div>
                {isFieldVisible('ssn') && (
                <div>
                  <Label>SSN {isFieldRequired('ssn') && '*'}</Label>
                  <DebouncedInput
                    value={formData.ssn || ''}
                    onChange={(e) => handleFieldChange('ssn', e.target.value)}
                    onBlur={(e) => handleIntakeFieldValidation('ssn', validateSSN(e.target.value))}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    placeholder="XXX-XX-XXXX"
                    className={intakeValidationErrors.ssn ? 'border-red-500' : ''}
                  />
                  {intakeValidationErrors.ssn && (
                    <p className="text-sm text-red-600 mt-1">{intakeValidationErrors.ssn}</p>
                  )}
                </div>
                )}
                {isFieldVisible('gender') && (
                <div>
                  <Label>Sex (Legal) {isFieldRequired('gender') && '*'}</Label>
                  <Select
                    value={formData.gender || ''}
                    onValueChange={(value) => handleFieldChange('gender', value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select sex" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="unknown">Unknown</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">Used for insurance eligibility &amp; EDI billing.</p>
                </div>
                )}
                {isFieldVisible('gender_identity') && (
                <div>
                  {/* Feb 2026 — Gender Identity (separate from legal sex).
                      Optional, informational only — NEVER sent in EDI/FHIR. */}
                  <Label>Gender Identity <span className="text-xs text-slate-400 font-normal">(optional)</span></Label>
                  <Select
                    value={formData.gender_identity || ''}
                    onValueChange={(value) => handleFieldChange('gender_identity', value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="How they identify" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="female">Identifies as Female</SelectItem>
                      <SelectItem value="male">Identifies as Male</SelectItem>
                      <SelectItem value="non-binary">Non-binary</SelectItem>
                      <SelectItem value="trans-female">Trans Female</SelectItem>
                      <SelectItem value="trans-male">Trans Male</SelectItem>
                      <SelectItem value="genderqueer">Genderqueer</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                      <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Email</Label>
                  <DebouncedInput
                    type="email"
                    value={formData.email || ''}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    onBlur={(e) => handleIntakeFieldValidation('email', validateEmail(e.target.value))}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    className={intakeValidationErrors.email ? 'border-red-500' : ''}
                  />
                  {intakeValidationErrors.email && (
                    <p className="text-sm text-red-600 mt-1">{intakeValidationErrors.email}</p>
                  )}
                </div>
                <div>
                  <Label>Phone *</Label>
                  <DebouncedInput
                    value={formData.phone || ''}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    onBlur={(e) => handleIntakeFieldValidation('phone', validatePhone(e.target.value))}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    placeholder="(555) 123-4567"
                    className={intakeValidationErrors.phone ? 'border-red-500' : ''}
                  />
                  {intakeValidationErrors.phone && (
                    <p className="text-sm text-red-600 mt-1">{intakeValidationErrors.phone}</p>
                  )}
                </div>
                <div>
                  <Label>Secondary Phone</Label>
                  <DebouncedInput
                    value={formData.phone_secondary || ''}
                    onChange={(e) => handleFieldChange('phone_secondary', e.target.value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <Label>Client Status *</Label>
                <Select
                  value={formData.status || 'pending_intake'}
                  onValueChange={(value) => handleFieldChange('status', value)}
                  disabled={editingClient && !canEditFieldCategory('status')}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_intake">Pending Waitlist Approval</SelectItem>
                    <SelectItem value="waitlist">Waitlist</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="discharged">Discharged</SelectItem>
                    {hasPermission('archive_clients') && (
                      <SelectItem value="archived">Archived</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Address Line 1 *</Label>
                  <DebouncedInput
                    value={formData.address_line1 || ''}
                    onChange={(e) => handleFieldChange('address_line1', e.target.value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  />
                </div>
                <div>
                  <Label>Address Line 2</Label>
                  <DebouncedInput
                    value={formData.address_line2 || ''}
                    onChange={(e) => handleFieldChange('address_line2', e.target.value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <DebouncedInput
                      value={formData.city || ''}
                      onChange={(e) => handleFieldChange('city', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <DebouncedInput
                      value={formData.state || ''}
                      onChange={(e) => handleFieldChange('state', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      placeholder="MA"
                    />
                  </div>
                  <div>
                    <Label>Zip Code *</Label>
                    <DebouncedInput
                      value={formData.zipcode || ''}
                      onChange={(e) => clientZip.handleZipChange(e.target.value)}
                      onBlur={(e) => handleIntakeFieldValidation('zipcode', validateZipCode(e.target.value))}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      placeholder="12345 or 12345-6789"
                      className={intakeValidationErrors.zipcode ? 'border-red-500' : ''}
                      data-testid="intake-client-zip"
                    />
                    {intakeValidationErrors.zipcode && (
                      <p className="text-sm text-red-600 mt-1">{intakeValidationErrors.zipcode}</p>
                    )}
                    {!intakeValidationErrors.zipcode && clientZip.autofillStatus === 'filled' && (
                      <p className="text-xs text-emerald-600 mt-1" data-testid="zip-autofilled">
                        Auto-filled city/state from ZIP.
                      </p>
                    )}
                    {!intakeValidationErrors.zipcode && (() => {
                      // Soft MA-only warning: MA zip codes fall in 010xx–027xx.
                      // Non-blocking; purely a typo heuristic since practice is MA-restricted.
                      const z = (formData.zipcode || '').trim();
                      if (!/^\d{5}/.test(z)) return null;
                      const prefix = parseInt(z.slice(0, 3), 10);
                      if (prefix >= 10 && prefix <= 27) return null;
                      return (
                        <p className="text-xs text-amber-600 mt-1" data-testid="zip-non-ma-warning">
                          Double-check: this zip isn't in MA (practice is MA-restricted).
                        </p>
                      );
                    })()}
                  </div>
                </div>
              </div>

              {/* Insurance Address Section */}
              <div className="space-y-4 border-t pt-4 mt-4">
                <div className="flex items-center space-x-2">
                  <input
                    id="use-separate-insurance-address"
                    type="checkbox"
                    checked={formData.use_separate_insurance_address || false}
                    onChange={(e) => handleFieldChange('use_separate_insurance_address', e.target.checked)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  />
                  <Label htmlFor="use-separate-insurance-address" className="cursor-pointer text-sm font-medium">
                    Insurance has different address on file (for claims)
                  </Label>
                </div>
                
                {formData.use_separate_insurance_address && (
                  <div className="pl-6 space-y-4 bg-blue-50 p-4 rounded-md">
                    <p className="text-sm text-blue-700 mb-2">
                      <strong>Note:</strong> This address will be used for insurance claims and must match what the insurance company has on file.
                    </p>
                    <div>
                      <Label>Insurance Address Line 1 *</Label>
                      <DebouncedInput
                        value={formData.insurance_address_line1 || ''}
                        onChange={(e) => handleFieldChange('insurance_address_line1', e.target.value)}
                        disabled={editingClient && !canEditFieldCategory('demographics')}
                      />
                    </div>
                    <div>
                      <Label>Insurance Address Line 2</Label>
                      <DebouncedInput
                        value={formData.insurance_address_line2 || ''}
                        onChange={(e) => handleFieldChange('insurance_address_line2', e.target.value)}
                        disabled={editingClient && !canEditFieldCategory('demographics')}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>City *</Label>
                        <DebouncedInput
                          value={formData.insurance_city || ''}
                          onChange={(e) => handleFieldChange('insurance_city', e.target.value)}
                          disabled={editingClient && !canEditFieldCategory('demographics')}
                        />
                      </div>
                      <div>
                        <Label>State *</Label>
                        <DebouncedInput
                          value={formData.insurance_state || ''}
                          onChange={(e) => handleFieldChange('insurance_state', e.target.value)}
                          disabled={editingClient && !canEditFieldCategory('demographics')}
                          placeholder="MA"
                        />
                      </div>
                      <div>
                        <Label>Zip Code *</Label>
                        <DebouncedInput
                          value={formData.insurance_zipcode || ''}
                          onChange={(e) => insuranceZip.handleZipChange(e.target.value)}
                          disabled={editingClient && !canEditFieldCategory('demographics')}
                          placeholder="12345 or 12345-6789"
                          data-testid="intake-insurance-zip"
                        />
                        {insuranceZip.autofillStatus === 'filled' && (
                          <p className="text-xs text-emerald-600 mt-1" data-testid="insurance-zip-autofilled">
                            Auto-filled city/state from ZIP.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-3 gap-4">
                {isFieldVisible('race') && (
                  <div>
                    <Label>Race {isFieldRequired('race') && '*'}</Label>
                    <DebouncedInput
                      value={formData.race || ''}
                      onChange={(e) => handleFieldChange('race', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      required={isFieldRequired('race')}
                    />
                  </div>
                )}
                {isFieldVisible('ethnicity') && (
                  <div>
                    <Label>Ethnicity {isFieldRequired('ethnicity') && '*'}</Label>
                    <DebouncedInput
                      value={formData.ethnicity || ''}
                      onChange={(e) => handleFieldChange('ethnicity', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      required={isFieldRequired('ethnicity')}
                    />
                  </div>
                )}
                <div>
                  <Label>Language</Label>
                  <Select
                    value={formData.language || ''}
                    onValueChange={(value) => handleFieldChange('language', value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="English">English</SelectItem>
                      <SelectItem value="Spanish">Spanish</SelectItem>
                      <SelectItem value="Portuguese">Portuguese</SelectItem>
                      <SelectItem value="Vietnamese">Vietnamese</SelectItem>
                      <SelectItem value="Albanian">Albanian</SelectItem>
                      <SelectItem value="Chinese (Mandarin)">Chinese (Mandarin)</SelectItem>
                      <SelectItem value="Arabic">Arabic</SelectItem>
                      <SelectItem value="Polish">Polish</SelectItem>
                      <SelectItem value="French">French</SelectItem>
                      <SelectItem value="Russian">Russian</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {formData.language === 'Other' && (
                <div>
                  <Label>Specify Language</Label>
                  <DebouncedInput
                    value={formData.language_other || ''}
                    onChange={(e) => handleFieldChange('language_other', e.target.value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                    placeholder="Enter language"
                  />
                </div>
              )}
              {formData.language && formData.language !== 'Other' && formData.language !== 'English' && (
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="interpreter_needed"
                    checked={formData.interpreter_needed || false}
                    onChange={(e) => handleFieldChange('interpreter_needed', e.target.checked)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  />
                  <Label htmlFor="interpreter_needed">Interpreter Needed?</Label>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                {isFieldVisible('occupation') && (
                  <div>
                    <Label>Occupation {isFieldRequired('occupation') && '*'}</Label>
                    <DebouncedInput
                      value={formData.occupation || ''}
                      onChange={(e) => handleFieldChange('occupation', e.target.value)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                      required={isFieldRequired('occupation')}
                    />
                  </div>
                )}
                <div>
                  <Label>Marital Status</Label>
                  <Select
                    value={formData.marital_status || ''}
                    onValueChange={(value) => handleFieldChange('marital_status', value)}
                    disabled={editingClient && !canEditFieldCategory('demographics')}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single</SelectItem>
                      <SelectItem value="married">Married</SelectItem>
                      <SelectItem value="divorced">Divorced</SelectItem>
                      <SelectItem value="widowed">Widowed</SelectItem>
                      <SelectItem value="separated">Separated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

          {/* Section 2: Guardian Information */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold">Guardian Information</h3>
              {!canEditFieldCategory('demographics') && editingClient && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={() => {
                    setShowChangeRequestDialog(true); setChangeRequestProposedChanges({});
                    setChangeRequestSection('demographics');
                    setChangeRequestFields(['has_guardian', 'guardian_name', 'guardian_phone', 'guardian_email', 'guardian_same_household']);
                  }}
                >
                  Request Change
                </Button>
              )}
            </div>
              <div className="flex items-center space-x-2 mb-4">
                <input
                  type="checkbox"
                  id="has_guardian"
                  checked={formData.has_guardian || false}
                  onChange={(e) => handleFieldChange('has_guardian', e.target.checked)}
                  disabled={editingClient && !canEditFieldCategory('demographics')}
                />
                <Label htmlFor="has_guardian">
                  Is this a minor with a parent/legal guardian or an adult with a legal guardian?
                </Label>
              </div>

              {formData.has_guardian && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Parent/Guardian Name *</Label>
                      <DebouncedInput
                        value={formData.guardian_name || ''}
                        onChange={(e) => handleFieldChange('guardian_name', e.target.value)}
                        disabled={editingClient && !canEditFieldCategory('demographics')}
                      />
                    </div>
                    <div>
                      <Label>Guardian Phone *</Label>
                      <DebouncedInput
                        value={formData.guardian_phone || ''}
                        onChange={(e) => handleFieldChange('guardian_phone', e.target.value)}
                        disabled={editingClient && !canEditFieldCategory('demographics')}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Guardian Email</Label>
                      <DebouncedInput
                        type="email"
                        value={formData.guardian_email || ''}
                        onChange={(e) => handleFieldChange('guardian_email', e.target.value)}
                        disabled={editingClient && !canEditFieldCategory('demographics')}
                        placeholder="guardian@email.com"
                      />
                    </div>
                    <div className="flex items-center space-x-2 pt-6">
                      <input
                        type="checkbox"
                        id="guardian_same_household"
                        checked={formData.guardian_same_household || false}
                        onChange={(e) => handleFieldChange('guardian_same_household', e.target.checked)}
                        disabled={editingClient && !canEditFieldCategory('demographics')}
                      />
                      <Label htmlFor="guardian_same_household">Lives in the same household?</Label>
                    </div>
                  </div>
                  
                  {/* Guardian Address - shown when NOT living in same household */}
                  {!formData.guardian_same_household && (
                    <div className="space-y-4 pl-4 border-l-2 border-gray-200">
                      <p className="text-sm text-gray-600">Guardian's Address</p>
                      <div>
                        <Label>Address Line 1</Label>
                        <DebouncedInput
                          value={formData.guardian_address_line1 || ''}
                          onChange={(e) => handleFieldChange('guardian_address_line1', e.target.value)}
                          disabled={editingClient && !canEditFieldCategory('demographics')}
                        />
                      </div>
                      <div>
                        <Label>Address Line 2</Label>
                        <DebouncedInput
                          value={formData.guardian_address_line2 || ''}
                          onChange={(e) => handleFieldChange('guardian_address_line2', e.target.value)}
                          disabled={editingClient && !canEditFieldCategory('demographics')}
                        />
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>City</Label>
                          <DebouncedInput
                            value={formData.guardian_city || ''}
                            onChange={(e) => handleFieldChange('guardian_city', e.target.value)}
                            disabled={editingClient && !canEditFieldCategory('demographics')}
                          />
                        </div>
                        <div>
                          <Label>State</Label>
                          <DebouncedInput
                            value={formData.guardian_state || ''}
                            onChange={(e) => handleFieldChange('guardian_state', e.target.value)}
                            disabled={editingClient && !canEditFieldCategory('demographics')}
                            placeholder="MA"
                          />
                        </div>
                        <div>
                          <Label>Zip Code</Label>
                          <DebouncedInput
                            value={formData.guardian_zipcode || ''}
                            onChange={(e) => handleFieldChange('guardian_zipcode', e.target.value)}
                            disabled={editingClient && !canEditFieldCategory('demographics')}
                            placeholder="12345"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 mt-4">
                    <input
                      type="checkbox"
                      id="guardian_is_emergency_contact"
                      checked={formData.guardian_is_emergency_contact || false}
                      onChange={(e) => handleFieldChange('guardian_is_emergency_contact', e.target.checked)}
                      disabled={editingClient && !canEditFieldCategory('demographics')}
                    />
                    <Label htmlFor="guardian_is_emergency_contact">Also add as emergency contact?</Label>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Section 3: Referral Source */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold">Referral Source</h3>
              {!canEditFieldCategory('clinical') && editingClient && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={() => {
                    setShowChangeRequestDialog(true); setChangeRequestProposedChanges({});
                    setChangeRequestSection('clinical');
                    setChangeRequestFields(['referral_source', 'referral_type', 'referral_contact']);
                  }}
                >
                  Request Change
                </Button>
              )}
            </div>
              <div>
                <Label>Referral Source *</Label>
                <Select
                  value={formData.referral_source || ''}
                  onValueChange={(value) => handleFieldChange('referral_source', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select referral source" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Self">Self</SelectItem>
                    <SelectItem value="Family/Friend">Family/Friend</SelectItem>
                    <SelectItem value="Physician">Physician</SelectItem>
                    <SelectItem value="Hospital">Hospital</SelectItem>
                    <SelectItem value="School">School</SelectItem>
                    <SelectItem value="Teacher">Teacher</SelectItem>
                    <SelectItem value="Probation Officer">Probation Officer</SelectItem>
                    <SelectItem value="Attorney">Attorney</SelectItem>
                    <SelectItem value="Court/Legal">Court/Legal</SelectItem>
                    <SelectItem value="DCF">DCF</SelectItem>
                    <SelectItem value="Insurance Company">Insurance Company</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.referral_source && formData.referral_source !== 'Self' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Referral Contact Name</Label>
                      <DebouncedInput
                        value={formData.referral_contact || ''}
                        onChange={(e) => handleFieldChange('referral_contact', e.target.value)}
                        placeholder="Contact person's name"
                      />
                    </div>
                    <div>
                      <Label>Referral Organization</Label>
                      <DebouncedInput
                        value={formData.referral_organization || ''}
                        onChange={(e) => handleFieldChange('referral_organization', e.target.value)}
                        placeholder="Organization or agency name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Referral Phone</Label>
                      <DebouncedInput
                        value={formData.referral_phone || ''}
                        onChange={(e) => handleFieldChange('referral_phone', e.target.value)}
                        placeholder="(555) 123-4567"
                      />
                    </div>
                    <div>
                      <Label>Referral Email</Label>
                      <DebouncedInput
                        type="email"
                        value={formData.referral_email || ''}
                        onChange={(e) => handleFieldChange('referral_email', e.target.value)}
                        placeholder="email@example.com"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {/* Reason for Referral */}
              <div>
                <Label>Reason for Referral *</Label>
                <textarea
                  value={formData.reason_for_referral || ''}
                  onChange={(e) => handleFieldChange('reason_for_referral', e.target.value)}
                  className="w-full min-h-[100px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Please describe the reason for this referral"
                  required
                />
              </div>
              
              {/* Symptoms Checklist */}
              <div>
                <Label className="mb-2 block">Symptoms Checklist</Label>
                <div className="grid grid-cols-3 gap-2 p-4 border border-gray-200 rounded-md max-h-60 overflow-y-auto">
                  {SYMPTOM_OPTIONS.map(symptom => (
                    <label key={symptom} className={`flex items-center space-x-2 text-sm ${URGENT_SYMPTOMS.includes(symptom) ? 'font-medium' : ''}`}>
                      <input
                        type="checkbox"
                        checked={(formData.symptoms_checklist || []).includes(symptom)}
                        onChange={(e) => {
                          const currentSymptoms = formData.symptoms_checklist || [];
                          if (e.target.checked) {
                            handleFieldChange('symptoms_checklist', [...currentSymptoms, symptom]);
                          } else {
                            handleFieldChange('symptoms_checklist', currentSymptoms.filter(s => s !== symptom));
                          }
                        }}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className={URGENT_SYMPTOMS.includes(symptom) ? 'text-red-600' : ''}>
                        {symptom}
                        {URGENT_SYMPTOMS.includes(symptom) && ' ⚠️'}
                      </span>
                    </label>
                  ))}
                </div>
                
                {/* Urgent symptoms warning */}
                {(formData.symptoms_checklist || []).some(s => URGENT_SYMPTOMS.includes(s)) && (
                  <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">⚠️ Urgent Symptoms Reported</p>
                      <p className="text-sm text-red-700">
                        Client has indicated: {(formData.symptoms_checklist || []).filter(s => URGENT_SYMPTOMS.includes(s)).join(', ')}
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Consider flagging this client as urgent in the waitlist after saving.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

          {/* Section 4: Client Preferences */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Client Preferences</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Service Requested</Label>
                  <Select
                    value={formData.service_requested || ''}
                    onValueChange={(value) => handleFieldChange('service_requested', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select service" />
                    </SelectTrigger>
                    <SelectContent>
                      {serviceTypes.length > 0 ? (
                        serviceTypes.map(st => (
                          <SelectItem key={st.id} value={st.name}>{st.name}</SelectItem>
                        ))
                      ) : (
                        <>
                          <SelectItem value="Mental Health Evaluation">Mental Health Evaluation</SelectItem>
                          <SelectItem value="Outpatient Therapy">Outpatient Therapy</SelectItem>
                          <SelectItem value="Medication Management">Medication Management</SelectItem>
                          <SelectItem value="Community Support Program (CSP)">Community Support Program (CSP)</SelectItem>
                          <SelectItem value="In Home Therapy (IHT)">In Home Therapy (IHT)</SelectItem>
                          <SelectItem value="In Home Behavioral Services (IHBS)">In Home Behavioral Services (IHBS)</SelectItem>
                          <SelectItem value="Therapeutic Mentoring (TM)">Therapeutic Mentoring (TM)</SelectItem>
                          <SelectItem value="Substance Addiction Evaluation">Substance Addiction Evaluation</SelectItem>
                          <SelectItem value="First Offender MID Program">First Offender MID Program</SelectItem>
                          <SelectItem value="Second Offender Aftercare (SOA)">Second Offender Aftercare (SOA)</SelectItem>
                          <SelectItem value="Outpatient Group Therapy">Outpatient Group Therapy</SelectItem>
                          <SelectItem value="SOAP (Structured Outpatient Addiction Program)">SOAP (Structured Outpatient Addiction Program)</SelectItem>
                          <SelectItem value="Adolescent SOAP">Adolescent SOAP</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preferred Mode</Label>
                  <Select
                    value={formData.preferred_mode || ''}
                    onValueChange={(value) => handleFieldChange('preferred_mode', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no-preference">No Preference</SelectItem>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="telehealth">Telehealth</SelectItem>
                      <SelectItem value="hybrid">Hybrid (Both)</SelectItem>
                      <SelectItem value="home">Home Visit/Outreach</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Preferred Location</Label>
                  <Select
                    value={formData.preferred_location || ''}
                    onValueChange={(value) => handleFieldChange('preferred_location', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select location" />
                    </SelectTrigger>
                    <SelectContent>
                      {(locations || []).map(location => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Client Prefers to be Seen By</Label>
                  <Select
                    value={formData.preferred_provider_type || ''}
                    onValueChange={(value) => handleFieldChange('preferred_provider_type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Either">Either</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>When is it OK to call or leave a voicemail?</Label>
                  <Select
                    value={formData.ok_to_call_times || ''}
                    onValueChange={(value) => handleFieldChange('ok_to_call_times', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select preferred time" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AM">AM</SelectItem>
                      <SelectItem value="PM">PM</SelectItem>
                      <SelectItem value="Either/Both">Either/Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>For Outreach, are there pets in the home?</Label>
                  <Select
                    value={formData.pets_in_home || ''}
                    onValueChange={(value) => handleFieldChange('pets_in_home', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
                
              {formData.pets_in_home === 'Yes' && (
                <div>
                  <Label>Please describe the pets</Label>
                  <DebouncedInput
                    value={formData.pets_description || ''}
                    onChange={(e) => handleFieldChange('pets_description', e.target.value)}
                    placeholder="e.g., 2 large dogs, 1 cat"
                  />
                </div>
              )}

              {/* Communication Consent */}
              <div className="space-y-3 border-t pt-4 mt-4">
                <Label className="text-sm font-medium">Communication Preferences</Label>
                <p className="text-xs text-gray-500">Select how this client prefers to receive appointment reminders and notifications</p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="intake-email-consent"
                      checked={formData.email_consent !== undefined ? formData.email_consent : true}
                      onCheckedChange={(checked) => handleFieldChange('email_consent', checked)}
                    />
                    <Label htmlFor="intake-email-consent" className="text-sm font-normal cursor-pointer">
                      Email reminders and notifications
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="intake-sms-consent"
                      checked={formData.sms_consent || false}
                      onCheckedChange={(checked) => handleFieldChange('sms_consent', checked)}
                    />
                    <Label htmlFor="intake-sms-consent" className="text-sm font-normal cursor-pointer">
                      SMS/Text message reminders
                    </Label>
                  </div>
                </div>
              </div>
          </div>

          {/* Section 5: Medical Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Medical Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Does the client have a Primary Care Provider (PCP)?</Label>
                  <Select
                    value={formData.has_pcp || ''}
                    onValueChange={(value) => handleFieldChange('has_pcp', value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of Last Physical</Label>
                  <span className="date-empty-wrapper">
                  <Input
                    type="date"
                    value={formData.last_physical_date || ''}
                    data-empty={!formData.last_physical_date}
                    onChange={(e) => handleFieldChange('last_physical_date', e.target.value)}
                  />
                  </span>
                </div>
              </div>

              {formData.has_pcp === 'Yes' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Primary Care Provider Name *</Label>
                      <DebouncedInput
                        value={formData.pcp_name || ''}
                        onChange={(e) => handleFieldChange('pcp_name', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>PCP Phone *</Label>
                      <DebouncedInput
                        value={formData.pcp_phone || ''}
                        onChange={(e) => handleFieldChange('pcp_phone', e.target.value)}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>PCP Office Address</Label>
                    <DebouncedInput
                      value={formData.pcp_address || ''}
                      onChange={(e) => handleFieldChange('pcp_address', e.target.value)}
                    />
                  </div>
                </>
              )}

              <div>
                <Label>Medical Alerts / Allergies / Health Considerations</Label>
                <DebouncedTextarea
                  value={formData.medical_alerts || ''}
                  onChange={(e) => handleFieldChange('medical_alerts', e.target.value)}
                  placeholder="Please list any medical alerts, allergies, or health considerations that might impact care"
                  rows={3}
                />
              </div>

              <div>
                <Label>Allergies</Label>
                {editingClient?.id ? (
                  /* Jun 2026 — structured allergies (same editor as the
                     Meds & Vitals chart tab). Single source of truth;
                     the free-text summary below stays auto-synced. */
                  <AllergiesSection client={editingClient} />
                ) : (
                  <DebouncedTextarea
                    value={formData.allergies || ''}
                    onChange={(e) => handleFieldChange('allergies', e.target.value)}
                    placeholder="List any known allergies"
                    rows={2}
                  />
                )}
              </div>
              
              {/* Preferred Pharmacy */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-800 mb-3">Preferred Pharmacy</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Pharmacy Name</Label>
                    <DebouncedInput
                      value={formData.pharmacy_name || ''}
                      onChange={(e) => handleFieldChange('pharmacy_name', e.target.value)}
                      placeholder="e.g., CVS, Walgreens"
                    />
                  </div>
                  <div>
                    <Label>Pharmacy Phone</Label>
                    <DebouncedInput
                      value={formData.pharmacy_phone || ''}
                      onChange={(e) => handleFieldChange('pharmacy_phone', e.target.value)}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label>Pharmacy Fax</Label>
                    <DebouncedInput
                      value={formData.pharmacy_fax || ''}
                      onChange={(e) => handleFieldChange('pharmacy_fax', e.target.value)}
                      placeholder="(555) 123-4568"
                    />
                  </div>
                  <div>
                    <Label>Pharmacy Address</Label>
                    <DebouncedInput
                      value={formData.pharmacy_address || ''}
                      onChange={(e) => handleFieldChange('pharmacy_address', e.target.value)}
                      placeholder="123 Main St, City, ST"
                    />
                  </div>
                </div>
              </div>
              
              {/* Current Medications */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-gray-800 mb-3">Active Medications</h4>
                
                {editingClient?.id ? (
                  // Existing client: read-only table from clientMedications, use standard dialog to add
                  <div>
                    {(() => {
                      const activeMeds = (clientMedications || []).filter(m => m.client_id === editingClient.id && m.is_active === true);
                      return activeMeds.length > 0 ? (
                        <div className="bg-blue-50 border border-blue-200 rounded overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-white border-b border-blue-200">
                              <tr>
                                <th className="text-left p-2 text-teal-700 font-medium">Medication</th>
                                <th className="text-left p-2 text-teal-700 font-medium">Dosage</th>
                                <th className="text-left p-2 text-teal-700 font-medium">Frequency</th>
                                <th className="text-left p-2 text-teal-700 font-medium">Prescriber</th>
                                <th className="text-left p-2 text-teal-700 font-medium">Purpose</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white">
                              {activeMeds.map((med, idx) => (
                                <tr key={med.id || idx} className="border-b border-gray-100 last:border-0">
                                  <td className="p-2">{med.medication_name || med.name}</td>
                                  <td className="p-2">{med.dosage || '-'}</td>
                                  <td className="p-2">{med.frequency || '-'}</td>
                                  <td className="p-2">{med.prescriber || '-'}</td>
                                  <td className="p-2">{med.purpose || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="bg-blue-50 border border-blue-200 rounded p-4">
                          <p className="text-sm text-gray-500 italic">No medications on file</p>
                        </div>
                      );
                    })()}
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (setShowMedicationForm && setEditingMedication) {
                          setEditingMedication(null);
                          setShowMedicationForm(true);
                        }
                      }}
                      className="mt-3 text-teal-600 border-teal-300 hover:bg-teal-50"
                      data-testid="add-medication-btn"
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" />
                      Add Medication
                    </Button>
                  </div>
                ) : (
                  // New client: inline editable approach (no client ID yet for API)
                  <div>
                {(!formData.medications || formData.medications.length === 0) ? (
                  <div className="bg-blue-50 border border-blue-200 rounded p-4">
                    <p className="text-sm text-gray-500 italic">No medications on file</p>
                  </div>
                ) : (
                  <div className="bg-blue-50 border border-blue-200 rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-white border-b border-blue-200">
                        <tr>
                          <th className="text-left p-2 text-teal-700 font-medium">Medication</th>
                          <th className="text-left p-2 text-teal-700 font-medium">Dosage</th>
                          <th className="text-left p-2 text-teal-700 font-medium">Frequency</th>
                          <th className="text-left p-2 text-teal-700 font-medium">Prescriber</th>
                          <th className="text-left p-2 text-teal-700 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {formData.medications.map((med, index) => (
                          <tr key={med.id || index} className="border-b border-gray-100 last:border-0">
                            <td className="p-2">
                              <DebouncedInput
                                value={med.name || ''}
                                onChange={(e) => {
                                  const updated = [...formData.medications];
                                  updated[index] = { ...updated[index], name: e.target.value };
                                  handleFieldChange('medications', updated);
                                }}
                                placeholder="Medication name"
                                className="h-8 border-0 bg-transparent p-0 focus:ring-0"
                              />
                            </td>
                            <td className="p-2">
                              <DebouncedInput
                                value={med.dosage || ''}
                                onChange={(e) => {
                                  const updated = [...formData.medications];
                                  updated[index] = { ...updated[index], dosage: e.target.value };
                                  handleFieldChange('medications', updated);
                                }}
                                placeholder="e.g., 10mg"
                                className="h-8 border-0 bg-transparent p-0 focus:ring-0 w-24"
                              />
                            </td>
                            <td className="p-2">
                              <DebouncedInput
                                value={med.frequency || ''}
                                onChange={(e) => {
                                  const updated = [...formData.medications];
                                  updated[index] = { ...updated[index], frequency: e.target.value };
                                  handleFieldChange('medications', updated);
                                }}
                                placeholder="e.g., Daily"
                                className="h-8 border-0 bg-transparent p-0 focus:ring-0 w-28"
                              />
                            </td>
                            <td className="p-2">
                              <DebouncedInput
                                value={med.prescriber || ''}
                                onChange={(e) => {
                                  const updated = [...formData.medications];
                                  updated[index] = { ...updated[index], prescriber: e.target.value };
                                  handleFieldChange('medications', updated);
                                }}
                                placeholder="Dr. Name"
                                className="h-8 border-0 bg-transparent p-0 focus:ring-0 w-32"
                              />
                            </td>
                            <td className="p-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const updated = formData.medications.filter((_, i) => i !== index);
                                  handleFieldChange('medications', updated);
                                }}
                                className="text-red-500 hover:text-red-700 flex items-center gap-1 text-sm"
                              >
                                <X className="h-3.5 w-3.5" />
                                <span>Remove</span>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                      const currentMeds = formData.medications || [];
                      const newMed = {
                        id: `med_${Date.now()}`,
                        name: '',
                        dosage: '',
                        frequency: '',
                        prescriber: '',
                        is_active: true
                      };
                      handleFieldChange('medications', [...currentMeds, newMed]);
                  }}
                  className="mt-3 text-teal-600 border-teal-300 hover:bg-teal-50"
                  data-testid="add-medication-btn"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Medication
                </Button>
                  </div>
                )}
              </div>
            </div>

          {/* Section 6: Substance Recovery */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Substance Addiction Recovery</h3>
              
              <div>
                <Label>Is the client requesting services for substance addiction recovery?</Label>
                <Select
                  value={formData.requesting_substance_services || 'No'}
                  onValueChange={(value) => handleFieldChange('requesting_substance_services', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Yes">Yes</SelectItem>
                    <SelectItem value="No">No</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {formData.requesting_substance_services === 'Yes' && (
                <>
                  <div>
                    <Label>Does the client require a 24Q alcohol or drug assessment as a condition for certain sentences?</Label>
                    <Select
                      value={formData.requires_24q_assessment || ''}
                      onValueChange={(value) => handleFieldChange('requires_24q_assessment', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.requires_24q_assessment === 'Yes' && (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Assessment Date (Scheduled or Completed)</Label>
                        <span className="date-empty-wrapper">
                        <Input
                          type="date"
                          value={formData.assessment_date || ''}
                          data-empty={!formData.assessment_date}
                          onChange={(e) => handleFieldChange('assessment_date', e.target.value)}
                        />
                        </span>
                      </div>
                      <div>
                        <Label>Recorded BAC (if applicable)</Label>
                        <DebouncedInput
                          value={formData.recorded_bac || ''}
                          onChange={(e) => handleFieldChange('recorded_bac', e.target.value)}
                          placeholder="e.g., 0.08"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}

              {formData.requesting_substance_services === 'No' && (
                <p className="text-sm text-gray-600">No substance recovery services requested.</p>
              )}
            </div>

          {/* Section 7: Insurance Information */}
          <div className="space-y-6">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold">Insurance Information</h3>
              {!canEditFieldCategory('insurance') && editingClient && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={() => {
                    setShowChangeRequestDialog(true); setChangeRequestProposedChanges({});
                    setChangeRequestSection('insurance');
                    setChangeRequestFields(['insurances']);
                  }}
                >
                  Request Change
                </Button>
              )}
            </div>
              
              {/* Important Notice */}
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="text-sm font-medium text-yellow-800">
                  <strong>Important:</strong> We do not accept insurance for MID or Evaluations. The client is responsible for the full cost of these services.
                </p>
              </div>
              
              {/* Display Insurance Coverage */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">
                    {editingClient ? 'Current Insurance Coverage' : 'Insurance Coverage'}
                  </Label>
                  {/* Show Manage Insurance button for existing clients */}
                  {editingClient && canEditFieldCategory('insurance') && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => handleOpenInsuranceManagement(editingClient)}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="manage-insurance-btn-existing"
                    >
                      <CreditCard className="h-4 w-4 mr-2" />
                      Manage Insurance
                    </Button>
                  )}
                  {/* Show Add Insurance button for new clients */}
                  {!editingClient && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddPendingInsurance}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="add-insurance-btn-new"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Insurance
                    </Button>
                  )}
                </div>

                {/* EXISTING CLIENT: Show existing insurances */}
                {editingClient && (() => {
                  const clientInsurances = editingClient?.insurances || [];
                  const activeInsurances = clientInsurances
                    .filter(ins => ins.status === 'active')
                    .sort((a, b) => a.priority - b.priority);
                  
                  if (activeInsurances.length === 0) {
                    return (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <p className="text-sm text-gray-600">
                          <strong>Payment Type:</strong> Self Pay
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Click "Manage Insurance" to add insurance coverage.
                        </p>
                      </div>
                    );
                  }

                  return (
                    <>
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Payment Type:</strong> Has Insurance
                      </p>
                      <div className="space-y-3">
                        {activeInsurances.map((insurance) => {
                          const expired = isInsuranceExpired(insurance);
                          const endingSoon = isInsuranceEndingSoon(insurance);
                          return (
                          <div
                            key={insurance.id}
                            className={`border rounded-lg p-4 ${
                              expired
                                ? 'bg-red-50 border-red-300 ring-1 ring-red-300'
                                : priorityColors[insurance.priority] || 'bg-gray-50 border-gray-200'
                            }`}
                            data-testid={`intake-insurance-card-${insurance.id || insurance.priority}`}
                            data-coverage-state={expired ? 'expired' : endingSoon ? 'ending-soon' : 'active'}
                          >
                            {expired && (
                              <div
                                className="-mt-2 -mx-2 mb-3 px-3 py-2 bg-red-100 border border-red-300 rounded text-red-900 text-xs font-semibold flex items-center gap-2"
                                data-testid={`intake-insurance-expired-banner-${insurance.id || insurance.priority}`}
                              >
                                <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                                <span>
                                  EXPIRED — coverage ended {fmtLocalDate(insurance.end_date)}.  Do not bill DOS after this date on this insurance.
                                </span>
                              </div>
                            )}
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-2 flex-wrap">
                                  <Badge className="bg-white border border-gray-300 text-gray-800 font-semibold">
                                    {priorityLabels[insurance.priority] || `Priority ${insurance.priority}`}
                                  </Badge>
                                  <h4 className="font-semibold text-lg">
                                    {insurance.company}{insurance.plan ? ` - ${insurance.plan}` : ''}
                                  </h4>
                                  {expired && (
                                    <Badge className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                                      Expired
                                    </Badge>
                                  )}
                                  {!expired && endingSoon && (
                                    <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-medium">
                                      Ends {fmtLocalDate(insurance.end_date)}
                                    </Badge>
                                  )}
                                </div>
                                <Badge className={
                                  insurance.eligibility_status === 'active' || insurance.eligibility_status === 'verified' 
                                    ? 'bg-green-100 text-green-800'
                                    : insurance.eligibility_status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }>
                                  {insurance.eligibility_status || 'Pending'}
                                </Badge>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                                {insurance.member_id && (
                                  <p><span className="font-medium">Member ID:</span> {insurance.member_id}</p>
                                )}
                                {insurance.group_number && (
                                  <p><span className="font-medium">Group #:</span> {insurance.group_number}</p>
                                )}
                                {insurance.relationship_to_insured && (
                                  <p><span className="font-medium">Relationship:</span> {insurance.relationship_to_insured}</p>
                                )}
                                {insurance.copay_amount && (
                                  <p><span className="font-medium">Copay:</span> ${parseFloat(insurance.copay_amount).toFixed(2)}</p>
                                )}
                                {insurance.deductible_amount && (
                                  <p><span className="font-medium">Deductible:</span> ${parseFloat(insurance.deductible_amount).toFixed(2)}</p>
                                )}
                                {insurance.start_date && (
                                  <p><span className="font-medium">Effective:</span> {fmtLocalDate(insurance.start_date)}</p>
                                )}
                                {insurance.end_date && (
                                  <p className={expired ? 'text-red-700 font-semibold' : ''}>
                                    <span className="font-medium">Expires:</span> {fmtLocalDate(insurance.end_date)}
                                  </p>
                                )}
                              </div>

                              {insurance.insured_name && insurance.relationship_to_insured !== 'self' && (
                                <div className="mt-2 pt-2 border-t border-gray-300">
                                  <p className="text-xs text-gray-600">
                                    <strong>Insured Person:</strong> {insurance.insured_name}
                                    {insurance.insured_dob && ` (DOB: ${new Date(insurance.insured_dob).toLocaleDateString()})`}
                                  </p>
                                </div>
                              )}

                              {(insurance.card_front_url || insurance.card_back_url) && (
                                <div className="mt-2 pt-2 border-t border-gray-300">
                                  <p className="text-xs text-green-600">✓ Insurance card images uploaded</p>
                                </div>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        Use "Manage Insurance" button above to add, edit, or reorder insurance coverage.
                      </p>
                    </>
                  );
                })()}

                {/* NEW CLIENT: Show pending insurances */}
                {!editingClient && (
                  <>
                    {pendingInsurances.length === 0 ? (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <p className="text-sm text-gray-600">
                          <strong>Payment Type:</strong> Self Pay
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          Click "Add Insurance" to add insurance coverage for this new client.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Payment Type:</strong> Has Insurance ({pendingInsurances.length} policy/policies)
                        </p>
                        <div className="space-y-3">
                          {pendingInsurances
                            .sort((a, b) => a.priority - b.priority)
                            .map((insurance, index) => (
                            <div
                              key={index}
                              className={`border rounded-lg p-4 ${priorityColors[insurance.priority] || 'bg-gray-50 border-gray-200'}`}
                              data-testid={`pending-insurance-${index}`}
                            >
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center space-x-2">
                                    <Badge className="bg-white border border-gray-300 text-gray-800 font-semibold">
                                      {priorityLabels[insurance.priority] || `Priority ${insurance.priority}`}
                                    </Badge>
                                    <h4 className="font-semibold text-lg">
                                      {insurance.company}{insurance.plan ? ` - ${insurance.plan}` : ''}
                                    </h4>
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <Badge className="bg-yellow-100 text-yellow-800">
                                      Pending
                                    </Badge>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEditPendingInsurance(index)}
                                      data-testid={`edit-pending-insurance-${index}`}
                                    >
                                      <Edit className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleRemovePendingInsurance(index)}
                                      className="text-red-600 hover:text-red-700"
                                      data-testid={`remove-pending-insurance-${index}`}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-2 text-sm text-gray-700">
                                  {insurance.member_id && (
                                    <p><span className="font-medium">Member ID:</span> {insurance.member_id}</p>
                                  )}
                                  {insurance.group_number && (
                                    <p><span className="font-medium">Group #:</span> {insurance.group_number}</p>
                                  )}
                                  {insurance.relationship_to_insured && (
                                    <p><span className="font-medium">Relationship:</span> {insurance.relationship_to_insured}</p>
                                  )}
                                  {insurance.copay_amount && (
                                    <p><span className="font-medium">Copay:</span> ${parseFloat(insurance.copay_amount).toFixed(2)}</p>
                                  )}
                                </div>

                                {insurance.insured_name && insurance.relationship_to_insured !== 'self' && (
                                  <div className="mt-2 pt-2 border-t border-gray-300">
                                    <p className="text-xs text-gray-600">
                                      <strong>Insured Person:</strong> {insurance.insured_name}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          These insurance policies will be saved when the client is created.
                        </p>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>


          {/* Section 8: Emergency Contacts */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold">Emergency Contacts</h3>
              {!canEditFieldCategory('demographics') && editingClient && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={() => {
                    setShowChangeRequestDialog(true); setChangeRequestProposedChanges({});
                    setChangeRequestSection('demographics');
                    setChangeRequestFields(['emergency_contact_1_name', 'emergency_contact_1_relationship', 'emergency_contact_1_phone', 'emergency_contact_1_email', 'emergency_contact_2_name', 'emergency_contact_2_relationship', 'emergency_contact_2_phone', 'emergency_contact_2_email']);
                  }}
                >
                  Request Change
                </Button>
              )}
            </div>
              <p className="text-sm text-gray-600 mb-4">
                Add at least one emergency contact who can be reached if needed.
              </p>

              <div className={`border p-4 rounded space-y-4 ${formData.guardian_is_emergency_contact ? 'bg-teal-50 border-teal-200' : ''}`}>
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold">Contact 1</h4>
                  {formData.guardian_is_emergency_contact && (
                    <Badge className="bg-teal-600 text-white text-xs">
                      Linked to Guardian
                    </Badge>
                  )}
                </div>
                {formData.guardian_is_emergency_contact && (
                  <p className="text-xs text-teal-700 -mt-2">
                    This contact is auto-filled from the Guardian section. To edit, update the guardian information above or uncheck "Also add as emergency contact".
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <DebouncedInput
                      value={formData.guardian_is_emergency_contact ? (formData.guardian_name || '') : (formData.emergency_contact_1_name || '')}
                      onChange={(e) => handleFieldChange('emergency_contact_1_name', e.target.value)}
                      disabled={formData.guardian_is_emergency_contact}
                      className={formData.guardian_is_emergency_contact ? 'bg-gray-100' : ''}
                    />
                  </div>
                  <div>
                    <Label>Relationship *</Label>
                    {formData.guardian_is_emergency_contact ? (
                      <DebouncedInput
                        value="Guardian"
                        disabled
                        className="bg-gray-100"
                      />
                    ) : (
                      <Select
                        value={formData.emergency_contact_1_relationship || ''}
                        onValueChange={(value) => handleFieldChange('emergency_contact_1_relationship', value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Parent">Parent</SelectItem>
                          <SelectItem value="Spouse">Spouse</SelectItem>
                          <SelectItem value="Partner">Partner</SelectItem>
                          <SelectItem value="Sibling">Sibling</SelectItem>
                          <SelectItem value="Child">Child</SelectItem>
                          <SelectItem value="Friend">Friend</SelectItem>
                          <SelectItem value="Grandparent">Grandparent</SelectItem>
                          <SelectItem value="Aunt/Uncle">Aunt/Uncle</SelectItem>
                          <SelectItem value="Cousin">Cousin</SelectItem>
                          <SelectItem value="Guardian">Guardian</SelectItem>
                          <SelectItem value="Case Manager">Case Manager</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    {!formData.guardian_is_emergency_contact && formData.emergency_contact_1_relationship === 'Other' && (
                      <DebouncedInput
                        className="mt-2"
                        value={formData.emergency_contact_1_relationship_other || ''}
                        onChange={(e) => handleFieldChange('emergency_contact_1_relationship_other', e.target.value)}
                        placeholder="Specify relationship"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone *</Label>
                    <DebouncedInput
                      value={formData.guardian_is_emergency_contact ? (formData.guardian_phone || '') : (formData.emergency_contact_1_phone || '')}
                      onChange={(e) => handleFieldChange('emergency_contact_1_phone', e.target.value)}
                      disabled={formData.guardian_is_emergency_contact}
                      className={formData.guardian_is_emergency_contact ? 'bg-gray-100' : ''}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <DebouncedInput
                      type="email"
                      value={formData.guardian_is_emergency_contact ? (formData.guardian_email || '') : (formData.emergency_contact_1_email || '')}
                      onChange={(e) => handleFieldChange('emergency_contact_1_email', e.target.value)}
                      disabled={formData.guardian_is_emergency_contact}
                      className={formData.guardian_is_emergency_contact ? 'bg-gray-100' : ''}
                    />
                  </div>
                </div>
              </div>

              <div className="border p-4 rounded space-y-4">
                <h4 className="font-semibold">Contact 2 (Optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name</Label>
                    <DebouncedInput
                      value={formData.emergency_contact_2_name || ''}
                      onChange={(e) => handleFieldChange('emergency_contact_2_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Select
                      value={formData.emergency_contact_2_relationship || ''}
                      onValueChange={(value) => handleFieldChange('emergency_contact_2_relationship', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select relationship" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Parent">Parent</SelectItem>
                        <SelectItem value="Spouse">Spouse</SelectItem>
                        <SelectItem value="Partner">Partner</SelectItem>
                        <SelectItem value="Sibling">Sibling</SelectItem>
                        <SelectItem value="Child">Child</SelectItem>
                        <SelectItem value="Friend">Friend</SelectItem>
                        <SelectItem value="Grandparent">Grandparent</SelectItem>
                        <SelectItem value="Aunt/Uncle">Aunt/Uncle</SelectItem>
                        <SelectItem value="Cousin">Cousin</SelectItem>
                        <SelectItem value="Guardian">Guardian</SelectItem>
                        <SelectItem value="Case Manager">Case Manager</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.emergency_contact_2_relationship === 'Other' && (
                      <DebouncedInput
                        className="mt-2"
                        value={formData.emergency_contact_2_relationship_other || ''}
                        onChange={(e) => handleFieldChange('emergency_contact_2_relationship_other', e.target.value)}
                        placeholder="Specify relationship"
                      />
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <DebouncedInput
                      value={formData.emergency_contact_2_phone || ''}
                      onChange={(e) => handleFieldChange('emergency_contact_2_phone', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <DebouncedInput
                      type="email"
                      value={formData.emergency_contact_2_email || ''}
                      onChange={(e) => handleFieldChange('emergency_contact_2_email', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

          {/* Section 9: Provider Assignment */}
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-lg font-semibold">Provider Assignment</h3>
              {!canEditFieldCategory('provider_assignment') && editingClient && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-blue-600 border-blue-600 hover:bg-blue-50"
                  onClick={() => {
                    setShowChangeRequestDialog(true); setChangeRequestProposedChanges({});
                    setChangeRequestSection('provider_assignment');
                    setChangeRequestFields(['assigned_providers']);
                  }}
                >
                  Request Change
                </Button>
              )}
            </div>
              <p className="text-sm text-gray-600 mb-4">
                Choose whether to assign this client to a provider or add them to the waitlist.
              </p>
              
              <div className="space-y-4">
                {/* Assign Providers & Services */}
                {hasPermission('manage_service_enrollments') && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Assigned Providers & Services (Optional)</Label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={editingClient && !canEditFieldCategory('provider_assignment')}
                      onClick={async () => {
                        // Set selectedClient for the dialog
                        if (editingClient) {
                          setSelectedClient(editingClient);
                        } else if (intakeClientId) {
                          // Use the intake client ID for new clients in progress
                          try {
                            const response = await axios.get(`${API}/clients/${intakeClientId}`);
                            setSelectedClient(response.data);
                          } catch (error) {
                            console.error('Failed to fetch client:', error);
                            setSelectedClient({
                              id: intakeClientId,
                              full_name: formData.full_name || 'New Client',
                              assigned_providers: formData.assigned_providers || []
                            });
                          }
                        } else {
                          setSelectedClient({
                            id: 'new',
                            full_name: formData.full_name || 'New Client',
                            assigned_providers: formData.assigned_providers || []
                          });
                        }
                        
                        // Use editingClient's assigned_providers as the source of truth (synced with service enrollments)
                        // Fall back to intakeData for new clients
                        setSelectedProvidersForAssignment(editingClient?.assigned_providers || formData.assigned_providers || []);
                        
                        // Load providers if needed
                        if ((providers || []).length === 0) {
                          try {
                            void 0 && console.log('Fetching providers from:', `${API}/providers`);
                            const response = await axios.get(`${API}/providers`, {
                              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                            });
                            void 0 && console.log('Providers fetched:', response.data.length);
                            if (setProviders) setProviders(response.data);
                          } catch (error) {
                            console.error('Failed to fetch providers:', error);
                            toast.error('Failed to load providers');
                          }
                        } else {
                          void 0 && console.log('Using existing providers:', providers.length);
                        }
                        
                        // Load services
                        try {
                          const servicesResponse = await axios.get(`${API}/treatment-planning/service-types`, {
                            headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                          });
                          setAvailableServices(servicesResponse.data);
                        } catch (error) {
                          console.error('Failed to fetch services:', error);
                          toast.error('Failed to load services');
                        }
                        
                        // Load existing enrollments if editing and pre-populate assignments
                        if (editingClient?.id || intakeClientId) {
                          const clientId = editingClient?.id || intakeClientId;
                          try {
                            const enrollmentsResponse = await axios.get(`${API}/clients/${clientId}/service-enrollments`, {
                              headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` }
                            });
                            const enrollments = enrollmentsResponse.data;
                            setExistingEnrollments(enrollments);
                            
                            // Pre-populate providerServiceAssignments from existing active/assigned enrollments
                            const assignments = {};
                            const existingProviderIds = new Set();
                            enrollments.forEach(enrollment => {
                              if (enrollment.status === 'active' || enrollment.status === 'assigned') {
                                if (!assignments[enrollment.provider_id]) {
                                  assignments[enrollment.provider_id] = [];
                                }
                                assignments[enrollment.provider_id].push(enrollment.service_type);
                                existingProviderIds.add(enrollment.provider_id);
                              }
                            });
                            setProviderServiceAssignments(assignments);
                            
                            // Sync selectedProvidersForAssignment with existing enrollments
                            // This ensures the provider list includes all providers with active service enrollments
                            setSelectedProvidersForAssignment([...existingProviderIds]);
                            
                          } catch (error) {
                            console.error('Failed to fetch enrollments:', error);
                            setExistingEnrollments([]);
                            setProviderServiceAssignments({});
                          }
                        } else {
                          setExistingEnrollments([]);
                          setProviderServiceAssignments({});
                        }
                        
                        setShowAssignProviders(true);
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Assign Services
                    </Button>
                  </div>
                  
                  {/* Display provider/service summary */}
                  <div className="space-y-2">
                    {formData.assigned_providers && formData.assigned_providers.length > 0 ? (
                      <div className="border rounded-md p-3 bg-gray-50">
                        <p className="text-sm text-gray-600 mb-2">
                          {formData.assigned_providers.length} provider(s) assigned
                        </p>
                        <div className="space-y-1">
                          {formData.assigned_providers.map(providerId => {
                            const provider = (users || []).find(p => p.id === providerId);
                            if (!provider) return null;
                            
                            return (
                              <div key={providerId} className="flex items-center justify-between bg-white p-2 rounded text-sm">
                                <span>
                                  {provider.first_name} {provider.last_name} - {provider.title || 'Provider'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Click "Assign Services" to manage service enrollments
                        </p>
                      </div>
                    ) : (
                      <div className="border rounded-md p-3 text-center bg-gray-50">
                        <p className="text-sm text-gray-500">No providers assigned yet</p>
                        <p className="text-xs text-gray-400 mt-1">Click "Assign Services" to add providers and services</p>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            </div>

          {/* Section 10: Review & Confirm */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold border-b pb-2">Review & Confirm</h3>
              <div className="bg-gray-50 p-4 rounded space-y-2">
                {editingClient?.mrn && (
                  <p><strong>MRN:</strong> #{editingClient.mrn.replace('MRN-', '')}</p>
                )}
                {!editingClient?.mrn && intakeClientId && (
                  <p className="text-sm text-teal-600"><strong>MRN:</strong> Will be assigned upon completion</p>
                )}
                <p><strong>Name:</strong> {formData.full_name || 'Not provided'}</p>
                <p><strong>DOB:</strong> {formData.date_of_birth || 'Not provided'}</p>
                <p><strong>Phone:</strong> {formData.phone || 'Not provided'}{formData.phone_secondary ? ` / ${formData.phone_secondary} (alt)` : ''}</p>
                <p><strong>Email:</strong> {formData.email || 'Not provided'}</p>
                <p><strong>Address:</strong> {formData.address_line1 || 'Not provided'}</p>
                <p><strong>Communication Preferences:</strong> {
                  [
                    (formData.email_consent !== false) && 'Email',
                    formData.sms_consent && 'SMS'
                  ].filter(Boolean).join(', ') || 'None selected'
                }</p>
              </div>
              <p className="text-sm text-gray-600">
                Please review the information above. Click "{formData.status === 'waitlist' ? 'Add to Waitlist' : 'Complete Intake'}" to finish.
              </p>
            </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between mt-6 pt-4 border-t">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCloseIntakeWizard}
            >
              Close
            </Button>
            {editingClient && onViewChangeHistory && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-gray-500 hover:text-gray-700"
                onClick={() => onViewChangeHistory(editingClient)}
                data-testid="v2-intake-change-history-btn"
              >
                <History className="h-4 w-4 mr-1" />
                Change History
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            {!editingClient && (
              <Button
                type="button"
                variant="outline"
                onClick={() => handleSaveIntakeDraft(formData)}
              >
                Save Draft
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => handleGenerateIntakePDF(formData)}
            >
              📄 Generate PDF
            </Button>
            <Button
              type="button"
              onClick={() => handleCompleteIntake(formData)}
              disabled={intakeAutoSaving}
            >
              {intakeAutoSaving ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {editingClient ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingClient 
                  ? 'Update Client' 
                  : formData.status === 'waitlist' 
                    ? 'Add to Waitlist' 
                    : 'Complete Intake'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Add/Edit Insurance Form Dialog (for new clients) */}
    <Dialog open={showInsuranceForm} onOpenChange={setShowInsuranceForm}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingInsuranceIndex !== null ? 'Edit Insurance' : 'Add Insurance'}
          </DialogTitle>
          <DialogDescription>
            Enter the client's insurance information
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="ins-company">Insurance Company *</Label>
            <Select
              value={insuranceFormState.company}
              onValueChange={(value) => {
                const selectedIns = insuranceCompanies.find(ins => ins.name === value);
                setSelectedInsuranceCompany(selectedIns);
                setInsuranceFormState(prev => ({...prev, company: value, plan: ''}));
                setInsuranceSearchTerm('');
              }}
              onOpenChange={(open) => {
                if (!open) setInsuranceSearchTerm('');
              }}
            >
              <SelectTrigger id="ins-company" data-testid="insurance-company-select">
                <SelectValue placeholder="Search or select insurance company..." />
              </SelectTrigger>
              <SelectContent className="max-h-[400px]">
                <div className="p-2 sticky top-0 bg-white z-10 border-b">
                  <DebouncedInput
                    placeholder="Type to search insurances..."
                    value={insuranceSearchTerm}
                    onChange={(e) => setInsuranceSearchTerm(e.target.value)}
                    className="mb-2"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    data-testid="insurance-search-input"
                  />
                </div>
                {insuranceCompanies && insuranceCompanies.length > 0 ? (
                  <InsuranceCompanyOptions
                    companies={insuranceCompanies}
                    searchTerm={insuranceSearchTerm}
                  />
                ) : (
                  <div className="p-4 text-center text-gray-500">
                    Loading insurance companies...
                  </div>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Plan Selection */}
          {selectedInsuranceCompany?.plans && selectedInsuranceCompany.plans.length > 0 && (
            <div>
              <Label htmlFor="ins-plan">Insurance Plan</Label>
              <Select
                value={insuranceFormState.plan}
                onValueChange={(value) => setInsuranceFormState(prev => ({...prev, plan: value}))}
              >
                <SelectTrigger id="ins-plan">
                  <SelectValue placeholder="Select plan (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {selectedInsuranceCompany.plans.map((plan, index) => {
                    const planName = typeof plan === 'string' ? plan : plan.name;
                    const planSecId = typeof plan === 'object' ? plan.secondary_identifier : null;
                    return (
                      <SelectItem key={index} value={planName}>
                        {planName}{planSecId ? ` [${planSecId}]` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ins-member-id">Member ID *</Label>
              <DebouncedInput
                id="ins-member-id"
                value={insuranceFormState.member_id}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, member_id: e.target.value}))}
                required
                data-testid="insurance-member-id"
              />
            </div>
            <div>
              <Label htmlFor="ins-group-number">Group Number</Label>
              <DebouncedInput
                id="ins-group-number"
                value={insuranceFormState.group_number}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, group_number: e.target.value}))}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ins-phone">Insurance Phone</Label>
            <DebouncedInput
              id="ins-phone"
              value={insuranceFormState.phone}
              onChange={(e) => setInsuranceFormState(prev => ({...prev, phone: e.target.value}))}
              placeholder="(XXX) XXX-XXXX"
            />
          </div>

          <div>
            <Label htmlFor="ins-relationship">Relationship to Insured *</Label>
            <Select
              value={insuranceFormState.relationship_to_insured}
              onValueChange={(value) => setInsuranceFormState(prev => ({...prev, relationship_to_insured: value}))}
            >
              <SelectTrigger id="ins-relationship">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="self">Self</SelectItem>
                <SelectItem value="spouse">Spouse</SelectItem>
                <SelectItem value="child">Child</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {insuranceFormState.relationship_to_insured !== 'self' && (
            <>
              <div>
                <Label htmlFor="ins-insured-name">Insured Person's Name *</Label>
                <DebouncedInput
                  id="ins-insured-name"
                  value={insuranceFormState.insured_name}
                  onChange={(e) => setInsuranceFormState(prev => ({...prev, insured_name: e.target.value}))}
                  placeholder="Enter insured person name"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ins-insured-ssn">Insured's SSN</Label>
                  <DebouncedInput
                    id="ins-insured-ssn"
                    value={insuranceFormState.insured_ssn}
                    onChange={(e) => setInsuranceFormState(prev => ({...prev, insured_ssn: e.target.value}))}
                    placeholder="XXX-XX-XXXX"
                  />
                </div>
                <div>
                  <Label htmlFor="ins-insured-dob">Insured's Date of Birth</Label>
                  <span className="date-empty-wrapper">
                  <Input
                    id="ins-insured-dob"
                    type="date"
                    value={insuranceFormState.insured_dob}
                    data-empty={!insuranceFormState.insured_dob}
                    onChange={(e) => setInsuranceFormState(prev => ({...prev, insured_dob: e.target.value}))}
                  />
                  </span>
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="ins-priority">Priority *</Label>
            <Select
              value={String(insuranceFormState.priority)}
              onValueChange={(value) => setInsuranceFormState(prev => ({...prev, priority: parseInt(value)}))}
            >
              <SelectTrigger id="ins-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Primary (1)</SelectItem>
                <SelectItem value="2">Secondary (2)</SelectItem>
                <SelectItem value="3">Tertiary (3)</SelectItem>
                <SelectItem value="4">Quaternary (4)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ins-start-date">Effective Date</Label>
              <span className="date-empty-wrapper">
              <Input
                id="ins-start-date"
                type="date"
                value={insuranceFormState.start_date}
                data-empty={!insuranceFormState.start_date}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, start_date: e.target.value}))}
              />
              </span>
            </div>
            <div>
              <Label htmlFor="ins-end-date">Termination Date</Label>
              <span className="date-empty-wrapper">
              <Input
                id="ins-end-date"
                type="date"
                value={insuranceFormState.end_date}
                data-empty={!insuranceFormState.end_date}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, end_date: e.target.value}))}
              />
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="ins-copay">Client Copay Amount ($)</Label>
              <Input
                id="ins-copay"
                type="number"
                step="0.01"
                min="0"
                value={insuranceFormState.copay_amount}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, copay_amount: e.target.value}))}
                placeholder="0.00"
              />
            </div>
            <div>
              <Label htmlFor="ins-deductible">Client Deductible Amount ($)</Label>
              <Input
                id="ins-deductible"
                type="number"
                step="0.01"
                min="0"
                value={insuranceFormState.deductible_amount}
                onChange={(e) => setInsuranceFormState(prev => ({...prev, deductible_amount: e.target.value}))}
                placeholder="0.00"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-2 mt-6">
          <Button type="button" variant="outline" onClick={() => setShowInsuranceForm(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePendingInsurance} 
            className="bg-teal-600 hover:bg-teal-700"
            disabled={!insuranceFormState.company || !insuranceFormState.member_id}
            data-testid="save-insurance-btn"
          >
            {editingInsuranceIndex !== null ? 'Update Insurance' : 'Add Insurance'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default IntakeWizardDialog;
